import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { processGoogleCode, processSsoSession } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Accept session_id / code from BOTH query string and hash fragment
        // (some SSO hubs redirect with #session_id=..., others with ?session_id=...)
        const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
        const ssoSessionId = searchParams.get('session_id') || hashParams.get('session_id');
        const googleCode = searchParams.get('code');
        const errorParam = searchParams.get('error') || hashParams.get('error');

        if (errorParam) {
          setError('Sign in was cancelled');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        // FoundryAI SSO hub-initiated launch
        if (ssoSessionId) {
          await processSsoSession(ssoSessionId);
          toast.success('Signed in via FoundryAI SSO');
          // Strip session_id from URL before landing on dashboard
          window.history.replaceState({}, document.title, '/dashboard');
          navigate('/dashboard', { replace: true });
          return;
        }

        // Legacy Google OAuth code flow
        if (googleCode) {
          await processGoogleCode(googleCode);
          toast.success('Signed in with Google!');
          navigate('/dashboard', { replace: true });
          return;
        }

        setError('No authorization credentials received');
        setTimeout(() => navigate('/login'), 2000);
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.response?.data?.detail || err.message || 'Authentication failed');
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    processAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-2">{error}</p>
          <p className="text-zinc-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-violet-500 mx-auto mb-4" />
        <p className="text-zinc-400">Completing sign in...</p>
      </div>
    </div>
  );
}
